import type {FC} from 'react';
import {components, type OptionProps} from 'react-select';

import styled, {css} from 'styled-components';

import CheckSVG from '../../../../assets/icons/check.svg';
import {getColor, getFontWeight, ms} from '../../../../styles';
import {CheckboxV2} from '../../../Checkbox/v2/Checkbox.v2';
import {Icon} from '../../../Icon';
import {Text} from '../../../Text';
import {type SelectV2} from '../Select.v2';
import {getSelectSizeStyles} from '../Select.v2.styles';
import {useSelectV2OptionIcon} from './hooks/useSelectV2OptionIcon';
import {ThemeForwardingWrapper} from './SelectThemeForwarding.v2';

const ForwardedWrapper = ({forwardTheme, ...props}) => {
  return (
    <ThemeForwardingWrapper
      Component={components.Option}
      forwardTheme={forwardTheme}
      {...props}
    />
  );
};

const optionPrimary = css`
  color: ${getColor('button.fill1')};
  background-color: ${getColor('background3')};
`;

const optionSecondary = css`
  color: ${getColor('button.fill2')};
  background-color: ${getColor('background5')};
`;

const CheckMark = styled(Icon).attrs({
  color: 'text.black',
})`
  justify-self: flex-end;
`;

const Container = styled(ForwardedWrapper)<SelectOptionV2.Props>`
  && {
    color: ${getColor('text.black')};
    background-color: transparent;
    font-weight: ${getFontWeight('semibold')};
    display: flex;
    align-items: center;
    gap: ${ms(10)};
    border-bottom: ${ms(1)} solid ${getColor('border.light')};
    ${p => getSelectSizeStyles(p)};
    border-radius: 0;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      ${p => {
        switch (p.scheme) {
          case 'primary':
            return optionPrimary;
          case 'secondary':
            return optionSecondary;
          default:
            return optionPrimary;
        }
      }}
    }

    ${p =>
      (p.isSelected || p.isDisabled) &&
      css`
        color: ${getColor('text3')};
        background-color: transparent;

        &:hover {
          color: ${getColor('text3')};
          background-color: transparent;
        }
      `};

    ${p =>
      p.isFocused &&
      (() => {
        switch (p.scheme) {
          case 'primary':
            return optionPrimary;
          case 'secondary':
            return optionSecondary;
          default:
            return '';
        }
      })()};
  }
`;

export const SelectOptionV2: FC<SelectOptionV2.Props> = ({theme, ...rest}) => {
  const textColor = rest.isDisabled ? 'text.gray' : 'text.black';

  const {renderIcon} = useSelectV2OptionIcon({data: rest.data, textColor});

  const renderDefault = () => (
    <>
      <CheckboxV2
        aria-hidden={'true'}
        aria-label={'Selected'}
        checked={rest.isSelected}
      />

      <Text color={textColor}>{rest.label}</Text>
    </>
  );

  const renderSmall = () => (
    <>
      {renderIcon()}
      <Text
        color={textColor}
        variant={rest.size === 'xs' ? 'label.sm' : 'label.la'}
      >
        {rest.label}
      </Text>
      {rest.isSelected ? (
        <CheckMark size={rest.size === 'xs' ? 10 : 14} svg={CheckSVG} />
      ) : null}
    </>
  );

  return (
    <Container {...rest} forwardTheme={theme}>
      {rest.size === 'xs' || rest.size === 'sm'
        ? renderSmall()
        : renderDefault()}
    </Container>
  );
};

export namespace SelectOptionV2 {
  export type Props = OptionProps &
    Pick<SelectV2.Props, 'scheme'> &
    Required<Pick<SelectV2.Props, 'size'>>;
}
